import {
  addDays,
  addHours,
  addMonths,
  addYears,
  differenceInHours,
} from 'date-fns'
import { PrimaryEnergyFlow, EnergyFlow } from '../../../calculators/EnergyFlow'
import {
  reduceToSumOfItems,
  groupBy,
  createReduceToSumOfGroupItems,
} from '../../../util/array-utility'
import { formatDate } from '../../common/util/date-util'
import { TimeResolution } from './ChartTimeResolutionSelect'
import { TimeSpan } from './ChartTimeSpanSelect'
import type { EnergyWithName } from './use-chart-util'

type DataPoint = {
  x: number
  y: number
}

export function formatHour(
  selectedDate: Date,
  hour: number,
  dateFormat: string,
): string {
  const date = addHours(selectedDate, hour)
  return formatDate(date, dateFormat)
}

/** Get the properly grouped energies for charts */
export function getDemandEnergiesForCharts(
  demands: PrimaryEnergyFlow[],
  demandIdsToNames: Record<string, string>,
): EnergyWithName[] {
  const demandFlowsByName = groupBy(
    demands,
    (flow) => demandIdsToNames[flow.demandId],
  )

  return Object.entries(demandFlowsByName).reduce(
    (result: EnergyWithName[], [name, flows]: [string, EnergyFlow[]]) => {
      const { energyType } = flows[0]

      // Never mix energy types in a single line
      if (flows.some((flow) => flow.energyType !== energyType)) {
        return result
      }

      const totalFlow = {
        name,
        energyType,
        hourSeries: flows
          .map((flow) => flow.hourSeries)
          .reduce(reduceToSumOfItems, []),
      }

      return [...result, totalFlow]
    },
    [],
  )
}

export const timeSpanToTickDistanceMap: Record<TimeSpan, number> = {
  [TimeSpan.Year]: 24 * 31,
  [TimeSpan.Month]: 24,
  [TimeSpan.Day]: 1,
}

export const getEndDateTime = (
  timeSpan: TimeSpan,
  selectedDate: Date,
): Date => {
  switch (timeSpan) {
    case TimeSpan.Day: {
      return addDays(selectedDate, 1)
    }
    case TimeSpan.Month: {
      return addMonths(selectedDate, 1)
    }
    default: {
      return addYears(selectedDate, 1)
    }
  }
}

const monthsInYear = 12
const getMonthStartHourIndices = (startOfYearDate: Date): number[] =>
  new Array(monthsInYear)
    .fill(0)
    .map((_, month) =>
      differenceInHours(addMonths(startOfYearDate, month), startOfYearDate),
    )

/**
 * Note: This reduce can probably be optimised by
 * e.g. slicing with monthStartHourIndices
 */
export const createGetValuesByResolution =
  (startOfYearDate: Date) =>
  (values: number[], resolution: TimeResolution): number[] => {
    const monthStartIndices = getMonthStartHourIndices(startOfYearDate)
    return resolution === TimeResolution.Month
      ? values.reduce(createReduceToSumOfGroupItems(monthStartIndices), [])
      : values
  }

export const mapValuesToDataPoints = (
  value: number,
  index: number,
): DataPoint => ({
  x: index,
  y: value,
})
